import React from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { ConfirmInvoiceStatusDateModal } from "../ConfirmInvoiceStatusDateModal";

interface Props {
    show: boolean;
    selectedCount: number;
    isProcessing: boolean;
    /**
     * The latest `invoiceDate` across every selected invoice (native
     * invoices only — every other source has `invoiceDate: null` and
     * doesn't constrain this), so the picker disables dates before it —
     * matching the single-invoice "voidedAt on/after invoiceDate" rule
     * (see `assertDatesOnOrAfterInvoiceDate`) for the whole selection.
     */
    minDate?: string;
    /**
     * The latest period lock date across every selected invoice's entity
     * (see `getLatestLockDate`), so the picker disables dates locked for
     * ANY of them — not just one.
     */
    lockDate?: Date | null;
    /**
     * False while the selected entities' lock query is still loading — keeps
     * Confirm disabled until the latest lock date is known, so a locked-period
     * date can't be submitted during that window.
     */
    isLockResolved?: boolean;
    onCancel: () => void;
    onConfirm: (date: string) => void;
}

// Reuses the same date-selection modal the single-invoice status dropdown
// uses for Void (ConfirmInvoiceStatusDateModal) — see InvoiceStatusSelect.
// Both floors — the invoice-date one (minDate) and the period-lock one
// (lockDate) — are enforced here client-side up front, combined via
// ConfirmInvoiceStatusDateModal's `laterDateOnly(minDate, firstUnlockedDate)`
// so the effective floor is whichever is later across the whole selection.
export const BulkVoidInvoicesModal: React.FC<Props> = ({
    show,
    selectedCount,
    isProcessing,
    minDate,
    lockDate,
    isLockResolved,
    onCancel,
    onConfirm,
}) => (
    <ConfirmInvoiceStatusDateModal
        show={show}
        title={`Mark ${pluralizeWithCount("invoice", selectedCount)} void`}
        description="This can't be undone. Choose the date these invoices should be marked void."
        dateLabel="Void date"
        confirmLabel="Mark void"
        isProcessing={isProcessing}
        minDate={minDate}
        lockDate={lockDate}
        isLockResolved={isLockResolved}
        onCancel={onCancel}
        onConfirm={onConfirm}
    />
);
